import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { alertService } from '../../../services/alertService';

const AgentAssignmentDashboard = () => {
  const [agentWorkload, setAgentWorkload] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAgentWorkload = async () => {
    try {
      setLoading(true);
      const data = await alertService?.getAgentWorkload();
      setAgentWorkload(data);
    } catch (error) {
      console.error('Error fetching agent workload:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgentWorkload();
    const interval = setInterval(fetchAgentWorkload, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      available: 'text-success bg-success/10 border-success',
      busy: 'text-warning bg-warning/10 border-warning',
      offline: 'text-muted-foreground bg-secondary border-border'
    };
    return colors?.[status] || colors?.offline;
  };

  const getStatusIcon = (status) => {
    const icons = {
      available: 'CheckCircle',
      busy: 'Clock',
      offline: 'XCircle'
    };
    return icons?.[status] || 'XCircle';
  };

  const getRoleIcon = (role) => {
    const icons = {
      coordinator: 'Network',
      validator: 'ShieldCheck',
      analyst: 'BarChart3',
      auditor: 'FileSearch',
      specialist: 'Sparkles'
    };
    return icons?.[role] || 'User';
  };

  const getRoleColor = (role) => {
    const colors = {
      coordinator: 'bg-primary/10 text-primary border-primary/20',
      validator: 'bg-success/10 text-success border-success/20',
      analyst: 'bg-accent/10 text-accent border-accent/20',
      auditor: 'bg-warning/10 text-warning border-warning/20',
      specialist: 'bg-secondary text-foreground border-border'
    };
    return colors?.[role] || colors?.specialist;
  };

  const getWorkloadLevel = (count) => {
    if (count === 0) return { label: 'Idle', color: 'text-muted-foreground' };
    if (count <= 2) return { label: 'Light', color: 'text-success' };
    if (count <= 4) return { label: 'Moderate', color: 'text-warning' };
    return { label: 'Heavy', color: 'text-destructive' };
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-foreground mb-1">Agent Assignment Dashboard</h2>
        <p className="text-sm text-muted-foreground">Current workload distribution and availability status with automatic routing decisions</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Loading agent workload...</p>
          </div>
        </div>
      ) : agentWorkload?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Agent Data</h3>
          <p className="text-sm text-muted-foreground">Agent workload information will appear here once assignments are made</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Workload Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Total Agents</span>
                <Icon name="Users" size={16} className="text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold text-foreground">{agentWorkload?.length}</p>
            </div>
            <div className="bg-card border border-success/20 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-success">Available</span>
                <Icon name="CheckCircle" size={16} className="text-success" />
              </div>
              <p className="text-2xl font-bold text-success">
                {agentWorkload?.filter(a => a?.agentStatus === 'available')?.length}
              </p>
            </div>
            <div className="bg-card border border-warning/20 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-warning">Busy</span>
                <Icon name="Clock" size={16} className="text-warning" />
              </div>
              <p className="text-2xl font-bold text-warning">
                {agentWorkload?.filter(a => a?.agentStatus === 'busy')?.length}
              </p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Active Alerts</span>
                <Icon name="AlertTriangle" size={16} className="text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold text-foreground">
                {agentWorkload?.reduce((sum, agent) => sum + (agent?.activeAlertsCount || 0), 0)}
              </p>
            </div>
          </div>

          {/* Agent Cards */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">Agent Workload Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {agentWorkload?.map(agent => {
                const workloadLevel = getWorkloadLevel(agent?.activeAlertsCount);
                return (
                  <div key={agent?.id} className="bg-card border border-border rounded-lg p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      {/* Agent Avatar */}
                      <div className={`flex-shrink-0 w-14 h-14 rounded-lg border-2 flex items-center justify-center ${getRoleColor(agent?.agentRole)}`}>
                        <Icon name={getRoleIcon(agent?.agentRole)} size={24} />
                      </div>

                      {/* Agent Info */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="text-base font-semibold text-foreground">{agent?.agentName}</h4>
                            <p className="text-xs text-muted-foreground capitalize">{agent?.agentRole}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-lg border ${getStatusColor(agent?.agentStatus)}`}>
                            <Icon name={getStatusIcon(agent?.agentStatus)} size={12} className="inline mr-1" />
                            {agent?.agentStatus}
                          </span>
                        </div>

                        {/* Specializations */}
                        {agent?.specialization?.length > 0 && (
                          <div className="mb-3">
                            <div className="flex flex-wrap gap-1">
                              {agent?.specialization?.slice(0, 3)?.map((spec, idx) => (
                                <span key={idx} className="text-xs bg-secondary px-2 py-0.5 rounded">
                                  {spec}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Workload Metrics */}
                        <div className="grid grid-cols-3 gap-3 mb-3">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Active Alerts</p>
                            <p className={`text-lg font-bold ${workloadLevel?.color}`}>
                              {agent?.activeAlertsCount || 0}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Today's Tasks</p>
                            <p className="text-lg font-bold text-foreground">{agent?.totalAssignmentsToday || 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Avg Time</p>
                            <p className="text-lg font-bold text-foreground">{agent?.avgResolutionTimeMinutes || 0}m</p>
                          </div>
                        </div>

                        {/* Workload Bar */}
                        <div className="mb-2">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-muted-foreground">Workload</span>
                            <span className={`font-medium ${workloadLevel?.color}`}>{workloadLevel?.label}</span>
                          </div>
                          <div className="h-2 bg-secondary rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all ${
                                agent?.activeAlertsCount === 0
                                  ? 'bg-muted-foreground'
                                  : agent?.activeAlertsCount <= 2
                                  ? 'bg-success'
                                  : agent?.activeAlertsCount <= 4
                                  ? 'bg-warning' :'bg-destructive'
                              }`}
                              style={{ width: `${Math.min((agent?.activeAlertsCount / 6) * 100, 100)}%` }}
                            ></div>
                          </div>
                        </div>

                        {/* Last Assignment */}
                        {agent?.lastAssignmentAt && (
                          <p className="text-xs text-muted-foreground">
                            Last assignment: {new Date(agent?.lastAssignmentAt)?.toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Routing Insights */}
          <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Zap" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Automatic Routing Active</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  New alerts are automatically assigned to available agents with the lowest workload. 
                  Agents with "Available" status and matching role specialization are prioritized for assignment. 
                  Manual override is available for critical situations requiring specific agent expertise.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentAssignmentDashboard;